import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as d3 from 'd3';
import { HomeService } from 'src/app/services/home/home.service';
import {
  RevenuePotential,
  RevenuePotentialChartData,
} from './revenue-potential.interface';
@Component({
  selector: 'app-revenue-potential',
  templateUrl: './revenue-potential.component.html',
  styleUrls: ['./revenue-potential.component.scss'],
})
export class RevenuePotentialComponent implements OnInit {
  constructor(
    private homeServiceRef: HomeService,
    private route: ActivatedRoute
  ) {}
  @Input() name!: string;
  revenuePotential: any;
  loading: boolean = false;
  legends: string[] = ['TER', 'RGO'];
  colors: string[] = ['#71b06b', '#487dc1'];

  getMillion(val: number) {
    //if value is a number in ones say 1000000 for 1M
    // return (val / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    //if val is in Millions already say 1 for 1000000
    return (val).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  getChart(res: RevenuePotential[]) {
    let _this = this;
    this.revenuePotential = res.map(
      (revenue: RevenuePotential, index: number) => {
        return {
          group: revenue.ssl,
          ter: revenue.ter < 0 ? 0 : revenue.ter,
          rgo: revenue.rgo < 0 ? 0 : revenue.rgo,
          tooltip: revenue.toolTip,
        };
      }
    );
    const margin = { top: 10, right: 10, bottom: 40, left: 50 },
      width = 600,
      height = 220 - margin.top - margin.bottom;

    // append the svg object to the body of the page
    const svg: any = d3
      .select(`#${this.name}`)
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr(
        'viewBox',
        `0,0, ${width + margin.left + margin.right},${
          height + margin.top + margin.bottom
        }`
      )
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    let columns = ['group', 'ter', 'rgo'];
    // List of subgroups = header of the csv files = soil condition here
    let subgroups = columns.slice(1);
    // List of groups = species here = value of the first column called group -> I show them on the X axis
    let groups = d3.map(
      this.revenuePotential,
      function (d: RevenuePotentialChartData) {
        return d.group;
      }
    );
    // Add X axis
    let x = d3.scaleBand().domain(groups).range([0, width]).padding(0.4);
    svg
      .append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(d3.axisBottom(x).tickSizeOuter(0));

    // Add Y axis
    let maxValue =
      d3.max(this.revenuePotential, (d: any) => Math.max(d.rgo, d.ter)) || 1000;
    let y = d3.scaleLinear().domain([0, maxValue]).range([height, 0]);
    // svg.append('g').call(d3.axisLeft(y));

    svg.append('g').call(
      d3.axisLeft(y).tickFormat(function (d: any) {
        return '$' + _this.getMillion(d);
      })
    );
    const tooltip = d3
      .select(`#${this.name}`)
      .append('div')
      .attr('class', 'toolTip')
      .style('opacity', 0);
    // color palette = one color per subgroup
    let color = d3
      .scaleOrdinal()
      .domain(subgroups)
      .range(['#71b06b', '#487dc1']);

    //stack the data? --> stack per subgroup
    let stackedData = d3.stack().keys(subgroups)(this.revenuePotential);

    // Show the bars
    svg
      .append('g')
      .selectAll('g')
      .data(stackedData)
      .enter()
      .append('g')
      .attr('fill', (d: any) => color(d.key))
      .selectAll('rect')
      // enter a second time = loop subgroup per subgroup to add all rectangles
      .data(function (d: any) {
        return d;
      })
      .enter()
      .append('rect')
      .attr('x', (d: any) => x(d.data.group))      
      .attr('y', (d: any) => y(d[1]))
      .attr('height', function (d: any) {
        return y(d[0]) - y(d[1]);
      })
      .attr('width', x.bandwidth())
      .on('mouseover', (event: any, d: any) => {
        tooltip.transition().duration(200).style('opacity', 0.9);
        tooltip
          .html(
            `<span>${d.data.tooltip} - <strong>$${_this.getMillion(
              d.data.ter + d.data.rgo
            )}</strong></span><br/><small>TER - <strong>$${_this.getMillion(d.data.ter)}</strong>; RGO - <strong>$${_this.getMillion(d.data.rgo)}</strong></small>`
          )
          .style('left', `${event.layerX - 70}px`)
          .style('top', `${event.layerY}px`);
      })
      .on('mouseout', () =>
        tooltip.transition().duration(500).style('opacity', 0)
      );

    var legend = svg
      .append('g')
      .attr('class', 'legend')
      .attr('height', 100)
      .attr('width', 100)
      .attr('transform', 'translate(-5,' + (height + 35) + ')');
    const colors = this.colors;
    const legends = this.legends;
    legend
      .selectAll('rect')
      .data(this.legends)
      .enter()
      .append('rect')
      .attr('x', function (d: any, i: number) {
        var xPost = legendXPosition(legends, i, 6);
        return xPost;
      })
      .attr('y', -9)
      .attr('width', 15)
      .attr('height', 5)
      .style('fill', function (d: any, i: number) {
        var color = colors[i];
        return color;
      });

    legend
      .selectAll('text')
      .data(this.legends)
      .enter()
      .append('text')
      .attr('x', function (d: any, i: number) {
        if (legends) var xPost = legendXPositionText(legends, i, 14, 6);
        return xPost;
      })
      .attr('y', -1)
      .text((d: any) => d);

    function legendXPositionText(
      data: any,
      position: any,
      textOffset: any,
      avgFontWidth: any
    ) {
      return legendXPosition(data, position, avgFontWidth) + textOffset;
    }
    function legendXPosition(data: any, position: any, avgFontWidth: any) {
      if (position == 0) {
        return 0;
      } else {
        var xPostiion = 0;
        for (var i = 0; i < position; i++) {
          xPostiion += data[i].length * avgFontWidth + 40;
        }
        return xPostiion;
      }
    }
  }
  ngOnInit(): void {
    this.loading = true;
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.homeServiceRef.getRevenuePotential(id).subscribe((res) => {
        this.loading = false;
        this.getChart(res);
      });
    }
  }
}
